import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, X } from 'lucide-react';
import RightDrawer from '../../../../shared/components/form/RightDrawer';
import type { EpcProjectMembershipRow, EpcProjectOrgUnit } from '../../../../services/epcProjectService';
import type { EpcScheduleTaskRow } from '../../types/scheduleTask';
import { orgUnitName, taskAssignedUserId, taskOrgUnitId } from '../utils/epcScheduleDepartmentAssignment';
import { formatDateCell } from '../utils/epcScheduleDatesDisplay';
import { formatDurationCompact } from '../utils/epcScheduleMsProjectFormat';
import { epcScheduleTaskStableKey } from '../utils/epcScheduleTaskStableKey';
import { deriveTaskStatus } from '../utils/taskStatus';
import { buildResponsibilityChain } from './taskDetailModels';
import { TaskDependencyMap } from './TaskDependencyMap';
import { TaskMiniTimeline } from './TaskMiniTimeline';
import { TaskProgressCard } from './TaskProgressCard';
import { TaskResponsibilityChain } from './TaskResponsibilityChain';
import { TaskStatusChip } from './TaskStatusChip';

export type EpcScheduleTaskDetailPanelProps = {
  open: boolean;
  tasks: EpcScheduleTaskRow[];
  /** Entry row (from the table row click). Re-targets via dependency nodes stack on top. */
  index: number | null;
  orgUnits: EpcProjectOrgUnit[];
  memberships: EpcProjectMembershipRow[];
  messageSummaryByKey: Record<string, { total: number; unreadFromOthers: number }>;
  onClose: () => void;
  onOpenDiscussion: (taskKey: string, taskTitle: string, rowLabel: string) => void;
};

export default function EpcScheduleTaskDetailPanel({
  open,
  tasks,
  index,
  orgUnits,
  memberships,
  messageSummaryByKey,
  onClose,
  onOpenDiscussion,
}: EpcScheduleTaskDetailPanelProps) {
  /** Re-target stack — last entry is the task shown; empty = the entry row. */
  const [stack, setStack] = useState<number[]>([]);
  useEffect(() => {
    setStack([]);
  }, [index]);
  const current = stack.length > 0 ? stack[stack.length - 1]! : index;
  const task = current != null ? tasks[current] : undefined;
  const today = useMemo(() => new Date(), []);

  const backIndex = stack.length > 1 ? stack[stack.length - 2]! : stack.length === 1 ? index : null;
  const backName =
    backIndex != null ? String(tasks[backIndex]?.name ?? '').trim() || `Row ${backIndex + 1}` : '';

  if (current == null || !task) return <RightDrawer open={false} onClose={onClose}>{null}</RightDrawer>;

  const status = deriveTaskStatus(tasks, current, today);
  const dept = orgUnitName(orgUnits, taskOrgUnitId(task));
  const duration = formatDurationCompact(task.duration);
  const wbs = task.wbs != null && String(task.wbs) ? String(task.wbs) : '—';
  const taskKey = epcScheduleTaskStableKey(task);
  const messageCount = messageSummaryByKey[taskKey]?.total ?? 0;
  const rowLabel = `Row ${current + 1} · WBS ${wbs}`;
  const taskTitle = String(task.name ?? '').trim() || 'Task';

  return (
    <RightDrawer open={open} onClose={onClose} size="60">
      <div className="-m-6 flex min-h-full flex-col bg-[#FAFBFA]">
        <div className="flex items-start justify-between gap-3 border-b border-[#f1f5f9] bg-white px-4 py-3">
          <div className="min-w-0">
            {stack.length > 0 && backIndex != null ? (
              <button
                type="button"
                onClick={() => setStack((s) => s.slice(0, -1))}
                className="mb-1 inline-flex items-center gap-1 text-[10px] font-bold text-[#076333] hover:underline"
              >
                <ChevronLeft className="h-3 w-3" aria-hidden />
                Back to {backName}
              </button>
            ) : null}
            <p className="text-[10px] font-bold tracking-[0.08em] text-[#076333]">
              WBS {wbs} · TASK {task.id != null && task.id !== '' ? String(task.id) : current + 1}
            </p>
            <h3 className="mt-0.5 truncate text-[14.5px] font-bold text-slate-900" title={taskTitle}>
              {taskTitle}
            </h3>
            <p className="mt-0.5 text-[10.5px] text-slate-500">
              {dept || 'No department'} · {formatDateCell(task.start)} → {formatDateCell(task.finish)}
              {duration ? ` · ${duration}` : ''}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <TaskStatusChip status={status} />
            <button
              type="button"
              onClick={onClose}
              aria-label="Close task details"
              className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="flex flex-col gap-3 px-4 py-3.5">
          <TaskDependencyMap
            tasks={tasks}
            index={current}
            orgUnits={orgUnits}
            today={today}
            onRetarget={(i) => setStack((s) => [...s, i])}
          />
          <TaskMiniTimeline tasks={tasks} index={current} today={today} />
          <div className="grid gap-3 lg:grid-cols-[1.25fr_1fr]">
            <TaskResponsibilityChain
              rows={buildResponsibilityChain(memberships, orgUnits, taskOrgUnitId(task), taskAssignedUserId(task))}
            />
            <TaskProgressCard
              task={task}
              today={today}
              messageCount={messageCount}
              onOpenDiscussion={() => onOpenDiscussion(taskKey, taskTitle, rowLabel)}
            />
          </div>
        </div>
      </div>
    </RightDrawer>
  );
}
